import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import { countryGroup, studioEligibility } from "config/legal";
import { LegalCheckbox, LegalLinks } from "components/Legal/LegalConsent";
import {
  PLAN_TERMS_VERSION,
  contractLang,
  errorText,
  fixedT,
  formatPlanPrice,
  localeFor,
  planFeatures,
  priceLine,
  siteLang,
  tierLabel,
} from "./studioText";

/* The checkout panel of the plans page (docs/studio-plans-spec.md, W-4).

   In the order the customer meets it:
     1. the country they live in (radio buttons, prefilled only from the
        profile), with the billing-address rule
     2. the plan's terms, in Spanish for Mexico and Costa Rica whatever the
        site language (an English copy follows, for reading only), and an
        UNTICKED agree box naming the price
     3. the 18+ / country box, shown only once a country is chosen
     4. the seller, the security note and the policy links (?doc=es for MX/CR)
     5. "Continue to secure payment", aria-disabled until all of that is done

   termsText is built from the same lines the box shows, so what the backend
   keeps in the consent record is exactly what was on the page. */

export const CHECKOUT_HEADING_ID = "studio-checkout-title";
const HINT_ID = "studio-checkout-hint";
const COUNTRY_HINT_ID = "studio-checkout-country-hint";

/** The lines of the plan's terms, in the contract's language. */
export function termsLines(ct, { tierPlan, offer, country, price, merchant, locale }) {
  const plan = tierLabel(ct, tierPlan.tier);
  const refund = offer.refund || {};
  const fair = offer.fairUse || {};
  return [
    priceLine(ct, price, locale),
    ct("studio.plans.terms.charge"),
    ct("studio.plans.terms.plan", { plan, features: planFeatures(ct, tierPlan).join(", ") }),
    ct("studio.plans.terms.fairUse", {
      gb: fair.gbPerApp ?? 20,
      reads: new Intl.NumberFormat(locale).format(fair.readsPerApp ?? 3000000),
    }),
    merchant === "link" ? ct("studio.plans.terms.cancelLink") : ct("studio.plans.terms.cancel"),
    ct("studio.plans.terms.downgrade"),
    ct("studio.plans.terms.lapse"),
    ct("studio.plans.terms.reminders"),
    ct("studio.plans.terms.priceChanges"),
    ct("studio.plans.terms.refund", {
      days: refund.firstPaymentDays ?? 10,
      max: refund.maxMessages ?? 20,
      renewalDays: refund.accidentalRenewalDays ?? 3,
    }),
    merchant === "link" ? ct("studio.plans.terms.sellerLink") : ct("studio.plans.terms.sellerCodenec"),
  ];
}

/** The agree box's sentence, by country. */
export const agreeText = (ct, country, price, plan) =>
  ct(`studio.plans.terms.agree${country === "MX" || country === "CR" ? country : "US"}`, { price, plan });

/** The seller line: "CODENEC, LLC · address · phone · complaints: email". */
export function sellerLine(ct, seller) {
  const parts = ["CODENEC, LLC"];
  if (seller?.address) parts.push(seller.address);
  if (seller?.phone) parts.push(seller.phone);
  if (seller?.complaintsEmail) parts.push(ct("studio.plans.seller.complaints", { email: seller.complaintsEmail }));
  return parts.join(" · ");
}

// The Studio group of the profile's residence, if it is one of `groups`.
// Old profiles that confirmed "I live in the US" read as US.
export function profileGroup(user, groups) {
  const acceptance = user?.legalAcceptance;
  const group = countryGroup(acceptance?.residenceCountry || (acceptance?.usResidentConfirmed ? "US" : ""));
  return group && groups.includes(group) ? group : null;
}

function TermsBox({ lines, agree, agreeInput }) {
  return (
    <div className="studio-plan-terms">
      <ul>
        {lines.map((line, i) => <li key={i}>{line}</li>)}
      </ul>
      {agreeInput || <p>{agree}</p>}
    </div>
  );
}

function StudioPlanCheckout({ tier, offer, onClose, onPricesChanged }) {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const headingRef = useRef(null);

  const groups = (offer?.countries || []).filter((c) => c.sellable).map((c) => c.code);
  const [country, setCountry] = useState(() => profileGroup(user, groups));
  const [agreed, setAgreed] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);
  const [termsStale, setTermsStale] = useState(false);

  // Focus the panel's heading when it opens, and when another plan is chosen.
  useEffect(() => {
    headingRef.current?.focus();
  }, [tier]);

  const lang = siteLang(i18n);
  const tierPlan = (offer?.plans || []).find((p) => p.tier === tier);
  if (!tierPlan) return null;

  const cLang = country ? contractLang(lang, country) : lang;
  const ct = fixedT(i18n, t, cLang);
  const locale = localeFor(cLang, country);
  const price = country ? tierPlan.prices?.[country] : null;
  const row = (offer.countries || []).find((c) => c.code === country);
  const merchant = row?.merchant || "codenec";
  const formatted = price ? formatPlanPrice(price, locale) : "";
  const planName = tierLabel(ct, tier);
  const lines = price ? termsLines(ct, { tierPlan, offer, country, price, merchant, locale }) : [];
  const agree = price ? agreeText(ct, country, formatted, planName) : "";
  const termsText = [...lines, agree].join("\n");
  // The English copy for an English-site visitor buying in Spanish.
  const enT = cLang === "es" && lang === "en" ? fixedT(i18n, t, "en") : null;
  const enLocale = localeFor("en", country);

  const missing = !country ? "country" : !price ? "price" : !agreed ? "agree" : !eligible ? "eligible" : null;
  const off = Boolean(missing) || working || termsStale;
  const hint = {
    country: t("studio.plans.checkout.hintCountry"),
    price: t("studio.plans.checkout.noPrice"),
    agree: t("studio.plans.checkout.hintAgree"),
    eligible: t("studio.plans.checkout.hintEligible"),
  }[missing];

  const chooseCountry = (code) => {
    setCountry(code);
    // A new price and a new statement: both are agreed again.
    setAgreed(false);
    setEligible(false);
    setError(null);
  };

  const start = async () => {
    if (off) return;
    setWorking(true);
    setError(null);
    try {
      const url = await studioApi.planCheckout(user, {
        tier,
        country,
        acceptedTerms: true,
        termsVersion: PLAN_TERMS_VERSION,
        termsText,
        price: { amount: price.amount, currency: price.currency, tax: price.tax },
        locale: cLang,
        eligibility: studioEligibility(country),
      });
      window.location.assign(url);
    } catch (err) {
      setWorking(false);
      // The terms are agreed again after any refusal; the 18+ / country
      // answer does not change, so it stays ticked.
      setAgreed(false);
      if (err?.code === "terms_changed") {
        setTermsStale(true);
      } else if (err?.code === "price_changed") {
        setError(errorText(t, err));
        onPricesChanged?.();
      } else {
        setError(errorText(t, err));
      }
    }
  };

  return (
    <section className="studio-plan-checkout" aria-labelledby={CHECKOUT_HEADING_ID} id="studio-plan-checkout">
      <div className="studio-plan-checkout__head">
        <h2 id={CHECKOUT_HEADING_ID} ref={headingRef} tabIndex={-1}>
          {t("studio.plans.checkout.title", { plan: tierLabel(t, tier) })}
        </h2>
        <button type="button" className="studio-btn studio-btn-small" onClick={onClose}>
          {t("studio.plans.checkout.close")}
        </button>
      </div>

      <fieldset className="studio-fieldset" aria-describedby={COUNTRY_HINT_ID}>
        <legend>{t("studio.plans.checkout.countryLegend")}</legend>
        {groups.map((code) => (
          <label key={code} className="studio-radio">
            <input
              type="radio"
              name="studio-checkout-country"
              value={code}
              checked={country === code}
              onChange={() => chooseCountry(code)}
              required
              disabled={working}
            />
            <span>{t(`studio.plans.countries.${code}`)}</span>
          </label>
        ))}
        <p id={COUNTRY_HINT_ID} className="studio-muted">{t("studio.plans.checkout.countryHint")}</p>
      </fieldset>

      {termsStale && (
        <div className="studio-notice" role="alert">
          <p>{t("studio.plans.checkout.termsReload")}</p>
          <button type="button" className="studio-btn studio-btn-small" onClick={() => window.location.reload()}>
            {t("studio.plans.checkout.reload")}
          </button>
        </div>
      )}

      {country && price && (
        <div lang={cLang}>
          <h3>{ct("studio.plans.checkout.termsTitle")}</h3>
          <TermsBox
            lines={lines}
            agreeInput={
              <LegalCheckbox
                id="studio-plan-agree"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                disabled={working || termsStale}
              >
                {agree}
              </LegalCheckbox>
            }
          />
          {/* Outside the terms on purpose: termsText must not change with it. */}
          <LegalCheckbox
            id="studio-plan-eligible"
            checked={eligible}
            onChange={(e) => setEligible(e.target.checked)}
            disabled={working || termsStale}
          >
            {ct(`legal.studioEligibility.${country}`)}
          </LegalCheckbox>
          <div className="studio-plan-seller">
            <p>{sellerLine(ct, offer.seller)}</p>
            <p>{ct("studio.plans.checkout.security")}</p>
            {/* [LAWYER: confirm the PROFECO wording and whether its contact details must be shown.] */}
            {country === "MX" && <p>{ct("studio.plans.checkout.profeco")}</p>}
          </div>
        </div>
      )}

      {enT && price && (
        <details className="studio-plan-english" lang="en">
          <summary>{enT("studio.plans.checkout.inEnglish")}</summary>
          <p className="studio-muted">{enT("studio.plans.checkout.englishNote")}</p>
          <TermsBox
            lines={termsLines(enT, { tierPlan, offer, country, price, merchant, locale: enLocale })}
            agree={agreeText(enT, country, formatPlanPrice(price, enLocale), tierLabel(enT, tier))}
          />
          <p>{enT(`legal.studioEligibility.${country}`)}</p>
        </details>
      )}

      {country && !price && <div className="studio-notice"><p>{t("studio.plans.checkout.noPrice")}</p></div>}

      <LegalLinks docLang={country === "MX" || country === "CR" ? "es" : undefined} />

      {error && <div className="studio-notice" role="alert"><p>{error}</p></div>}

      <button
        type="button"
        className="studio-btn studio-btn-primary"
        onClick={start}
        aria-disabled={off ? "true" : undefined}
        aria-describedby={hint && !termsStale ? HINT_ID : undefined}
      >
        {working ? t("studio.plans.checkout.redirecting") : t("studio.plans.checkout.continue")}
      </button>
      {hint && !termsStale && <p id={HINT_ID} className="studio-muted">{hint}</p>}
    </section>
  );
}

export default StudioPlanCheckout;
